// run-progress-lines.ts
//
// Phase 03 (R-15): pure formatters for the operator-facing progress lines
// `avatar autopilot run` prints while dispatching — a session can otherwise
// run for hours with zero console output. Called from runSupervisorUntilIdle
// (header) and supervisor-loop-attempt-start.ts/supervisor-loop-post-route-
// finalize.ts (▶/■) via `SupervisorTickInput.progress`. No I/O, no Date.now()
// — callers pass already-computed values so this stays trivially unit-testable.
import { usdCeilingOrUndefined } from "./schemas/config-budget-ceilings.js";
import type { RouteOutcomeResult } from "./supervisor-outcome-router.js";

/** Req 1: the FIRST progress line of a `run`, printed once before any ▶. */
export function formatRunHeaderLine(
  missionId: string,
  ticketCount: number,
  doneCount: number,
  todoCount: number,
): string {
  return (
    `Autopilot run ${missionId} — ${ticketCount} ticket ` +
    `(${doneCount} xong, ${todoCount} chờ). Theo dõi: avatar autopilot status --watch`
  );
}

/** 06g R5: USD with cents — the operator reads money, not token counts. */
export function formatUsd(usd: number): string {
  return `$${usd.toFixed(2)}`;
}

/** 06g R5: cumulative spend (undefined usage so far ⇒ $0.00) against the mission ceiling — 3.38.1: `limitUsd` absent ⇒ no USD ceiling. */
export interface CumulativeCostForLine {
  usd: number | undefined;
  limitUsd?: number;
}

/**
 * 3.38.1: the ▶ line's ceiling for a run — an explicit caller value wins;
 * otherwise the dispatch config's USD key: a ceiling ⇒ that number, a config
 * WITHOUT one ⇒ `null` (print spend, no `/$Y`), no config at all ⇒ undefined
 * (▶ prints no cost — pre-R5 callers/tests).
 */
export function costLimitForProgressLine(
  explicit: number | null | undefined,
  config: { max_cost_usd_per_mission?: number } | undefined,
): number | null | undefined {
  if (explicit !== undefined) return explicit;
  if (config === undefined) return undefined;
  const ceiling = usdCeilingOrUndefined(config.max_cost_usd_per_mission);
  return ceiling === undefined ? null : ceiling;
}

/** Req 2: printed right after attempt_started is committed (▶ start marker). 06g R5: `· lũy kế $A/$Y` when cost info is given; 3.38.1: `· lũy kế $A` when there is no USD ceiling. */
export function formatAttemptStartLine(
  ticketId: string,
  ticketKind: string,
  attemptNo: number,
  now: Date,
  cost?: CumulativeCostForLine,
): string {
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  const base = `▶ ${ticketId} ${ticketKind} · attempt ${attemptNo} · ${hh}:${mm}`;
  if (cost === undefined) return base;
  const ceiling = usdCeilingOrUndefined(cost.limitUsd);
  const spent = formatUsd(cost.usd === undefined ? 0 : cost.usd);
  return `${base} · lũy kế ${spent}${ceiling === undefined ? "" : `/$${ceiling}`}`;
}

/** Req 2: a short label for the route/verdict — the ■ line's arrow target. */
function shortRouteLabel(route: RouteOutcomeResult): string {
  if (route.kind === "done") return "done";
  if (route.kind === "quota_sleep") return "quota_sleep";
  if (route.kind === "needs_decision") return "needs_decision";
  // retry_decision: "blocked" | "escalate_waiting_human" | a retryable verdict
  // (retry) — decision.outcome carries the short label directly.
  return route.decision.outcome;
}

function formatDurationMmSs(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.round(elapsedMs / 1000));
  const mm = String(Math.floor(totalSeconds / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}

/**
 * Req 2: printed right after attempt_finished is committed (■ end marker).
 * Phase 04 (F1b): `stderrExcerpt` (≤120 chars, already extracted by
 * session-failure-reasons.ts) appends the worker's real failure text when
 * the attempt failed with one — undefined on every other route.
 */
export function formatAttemptEndLine(
  ticketId: string,
  route: RouteOutcomeResult,
  elapsedMs: number,
  stderrExcerpt?: string,
  attemptCostUsd?: number,
): string {
  // 06g R5: this attempt's own price (from the worker's `usage`), when reported.
  const cost = attemptCostUsd === undefined ? "" : ` · ${formatUsd(attemptCostUsd)}`;
  const base = `■ ${ticketId} → ${shortRouteLabel(route)} (${formatDurationMmSs(elapsedMs)})${cost}`;
  return stderrExcerpt === undefined ? base : `${base} — ${stderrExcerpt}`;
}
